import { ExtraDriverField } from '../../components/forms/types';
import { DriverDetailsFormItem } from '../../components/manage-drivers/types';
import { MotoringClaim, MotoringConviction } from '../../services/api';
import IDate from '../types/date';
import { getFormDate, getInitialDrivingLicence, isEmployed, isNotEmpty } from './utils';

export type DriverFormValues = {
	title: string;
	firstName: string;
	lastNames: string;
	dateOfBirth: IDate;
	maritalStatus: string;
	relationshipToProposer: string;
	isResidentFromBirth: string;
	ukResidenceSince: IDate | undefined;
	employmentType: string;
	occupation: string;
	industry: string;
	otherOccupation: string;
	otherIndustry: string;
	licenceType: string;
	licenceObtained: IDate;
	badgePassedDate?: IDate;
	licenceNumber: Record<string, string>;
	hasCriminalConvictions: string;
	hasBeenRefusedCover: string;
	accessToOtherVehicles: string;
	numberOfVehiclesOwned: string;
	numberOfVehiclesAccessTo: string;
	motoringConvictions: MotoringConviction[] | undefined;
	motoringClaims: MotoringClaim[] | undefined;
	otherEmployment: string;
	hasMotoringConvictions: string;
	hasMotoringClaims: string;
	driverUsage?: string;
	employmentStatus: string;
	otherEmploymentStatus: string;
	otherEmploymentType: string;
};

export const getDriverDefaultValues = (extraFields?: ExtraDriverField[]): DriverFormValues => {
	const initialValues: DriverFormValues = {
		title: '',
		firstName: '',
		lastNames: '',
		dateOfBirth: { day: '', month: '', year: '' },
		maritalStatus: '',
		relationshipToProposer: '',
		isResidentFromBirth: '',
		ukResidenceSince: undefined,
		employmentType: '',
		occupation: '',
		industry: '',
		otherOccupation: '',
		otherIndustry: '',
		licenceType: '',
		licenceObtained: { day: '', month: '', year: '' },
		licenceNumber: {
			'licenceField-1': '',
			'licenceField-2': '',
			'licenceField-3': '',
			'licenceField-4': '',
		},
		hasCriminalConvictions: '',
		hasBeenRefusedCover: '',
		accessToOtherVehicles: '',
		numberOfVehiclesOwned: '',
		numberOfVehiclesAccessTo: '',
		motoringConvictions: undefined,
		motoringClaims: undefined,
		driverUsage: undefined,
		otherEmployment: '',
		hasMotoringConvictions: '',
		hasMotoringClaims: '',
		employmentStatus: '',
		otherEmploymentStatus: '',
		otherEmploymentType: '',
	};
	if (extraFields?.includes('driverUsage')) {
		initialValues.driverUsage = '';
		initialValues.badgePassedDate = { day: '', month: '', year: '' };
	}
	return initialValues;
};

// Specifically to be used on the legacy Renewal/MTA journeys, on the full form
export const getDriverFormValues = (
	driver?: DriverDetailsFormItem,
	extraFields?: ExtraDriverField[],
): DriverFormValues => {
	const formData = getDriverDefaultValues(extraFields);
	if (!driver) return formData;

	formData.title = driver?.title || '';
	formData.firstName = driver?.firstName || '';
	formData.lastNames = driver?.lastNames || '';
	formData.dateOfBirth = getFormDate(driver?.dateOfBirth);
	formData.maritalStatus = driver?.maritalStatus || '';
	formData.relationshipToProposer = driver?.relationshipToProposer || '';
	formData.isResidentFromBirth = driver?.isResidentFromBirth ? 'yes' : 'no';
	formData.ukResidenceSince = getFormDate(driver?.ukResidenceSince);
	formData.employmentType = driver?.employmentType || '';
	formData.occupation = driver?.occupation || '';
	formData.industry = driver?.industry || '';
	formData.otherOccupation = driver?.otherOccupation || '';
	formData.otherIndustry = driver?.otherIndustry || '';
	// @ts-expect-error - value is used on the legacy submit, not the form
	formData.isMainDriver = (driver && (driver?.isMainDriver ? 'yes' : 'no')) || '';
	formData.licenceType = driver?.licenceType || '';
	formData.licenceObtained = getFormDate(driver?.licenceObtained);
	formData.licenceNumber = getInitialDrivingLicence(driver);
	formData.hasCriminalConvictions = driver?.hasCriminalConvictions ? 'yes' : 'no';
	formData.hasBeenRefusedCover = driver?.hasBeenRefusedCover ? 'yes' : 'no';
	formData.accessToOtherVehicles = driver?.accessToOtherVehicles ? 'yes' : 'no';
	formData.numberOfVehiclesOwned = driver?.numberOfVehiclesOwned?.toString() || '';
	formData.numberOfVehiclesAccessTo = driver?.numberOfVehiclesAccessTo?.toString() || '';
	formData.motoringConvictions = driver?.motoringConvictions?.length || 0 > 0 ? driver.motoringConvictions : undefined;
	formData.motoringClaims = driver.motoringClaims?.length || 0 > 0 ? driver.motoringClaims : undefined;
	formData.otherEmployment =
		driver && isNotEmpty(driver.otherOccupation) && isNotEmpty(driver.otherIndustry)
			? isNotEmpty(driver.otherEmploymentType) && isNotEmpty(driver.otherEmploymentStatus)
				? 'yes'
				: 'no'
			: 'no';
	formData.hasMotoringConvictions = (driver.motoringConvictions?.length as number) > 0 ? 'yes' : 'no';
	formData.hasMotoringClaims = (driver.motoringClaims?.length as number) > 0 ? 'yes' : 'no';
	formData.employmentStatus = isEmployed(driver?.employmentStatus);
	formData.otherEmploymentStatus = isEmployed(driver?.otherEmploymentStatus);
	formData.otherEmploymentType = driver?.otherEmploymentType || '';

	if (extraFields?.includes('driverUsage')) {
		formData.driverUsage = driver?.driverUsage || '';
		formData.badgePassedDate = getFormDate(driver?.badgePassedDate);
	}

	return formData;
};
